import React, { useState } from 'react';

import Field from '../../Fields/Field';
import InputTextField from '../../Fields/InputTextField';
import ActionButton from '../../Common/ActionButton';
import { queryGetCognitoSub, queryUpdateCognitoSub, queryUpdatePayorCognitoSub } from '../../../Queries';
import InputSelectField from '../../Fields/InputSelectField';

const SubscriptionDetailReassignPanel = ({ payor, subscriptionId, onCancelClick, refreshSubscription }) => {
  const [emailInput, setEmailInput] = useState(null);
  const [errors, setErrors] = useState([]);
  // search, confirm, success
  const [mode, setMode] = useState('SEARCH');
  const [level, setLevel] = useState('user');
  const [userId, setUserId] = useState(null);
  const [queryInProgress, setQueryInProgress] = useState(false);

  const searchEmailQuery = async (email) => {
    try {
      const response = await queryGetCognitoSub({ email });
      if (response.errors) throw response.errors.map(error => error.message);

      return response.data.getCognitoSub;
    } catch (errs) {
      throw Array.isArray(errs) ? errs : [errs];
    }
  }

  const saveEmailQuery = async (variables) => {
    try {
      const response = await queryUpdatePayorCognitoSub(variables);
      if (response.errors) throw response.errors.map(error => error.message);
    } catch (errs) {
      throw Array.isArray(errs) ? errs : [errs];
    }
  }

  const onCancel = () => {
    onCancelClick();
    setErrors([]);
    setMode('SEARCH');
  };

  const onSearch = async () => {
    setQueryInProgress(true);
    setErrors([]);

    try {
      const userId = await searchEmailQuery(emailInput);

      setUserId(userId);
      setMode('CONFIRM');
    } catch (errs) {
      setErrors(errs);
    } finally {
      setQueryInProgress(false);
    }
  };

  const onConfirm = async () => {
    const variables = {
      payor: {
        id: payor.fields.id.value,
        cognito_sub: payor.fields.cognito_sub.value
      },
      subscriptionId,
      level: level.toUpperCase(),
      newUserId: userId,
      newEmail: emailInput,
    }

    setQueryInProgress(true);
    try {
      await saveEmailQuery(variables);
      await refreshSubscription(subscriptionId);

      setMode('SUCCESS');
      setEmailInput(null);
      setUserId(null);
    } catch (errors) {
      console.error(errors);
      setErrors(errors);
    } finally {
      setQueryInProgress(false);
    }
  };

  const confirmMode = mode === 'CONFIRM';
  const successMode = mode === 'SUCCESS';

  let message;
  if (queryInProgress) {
    message = '';
  } else if (errors.length) {
    message = errors[0];
  } else if (confirmMode) {
    message = 'Success! Select “Confirm” to reassign this subscription.';
  } else if (successMode) {
    message = 'Email address has been updated.';
  } else {
    message = 'Enter the email address of the new primary contact. They must have a ParkChirp account.';
  }

  return (
    <div className="parkchirp--reassign-primary-contact">
      { message &&
        <div className="parkchirp--reassign-primary-contact">
          <span className="parkchirp--message--reassign-primary-contact--field">
            { message }
          </span>
        </div>
      }
      { !successMode &&
        <>
          <div className="parkchirp--reassign-primary-contact--field">
            <Field label="Email"
              id="parkchirp--payor--reassign-primary-contact"
              containerClasses={ ['parkchirp--contact-info-panel-field--reassign-primary-contact', 'parkchirp--reassign-primary-contact--control'] }
              layout="columns">
              <InputTextField inputId="parkchirp--payor--reassign-primary-contact"
                value={ emailInput }
                onChange={ (value) => setEmailInput(value) }
                editMode={ !confirmMode }
              />
            </Field>
          </div>
          { confirmMode &&
            <div className="parkchirp--reassign-primary-contact--field">
              <Field label="Operation"
                id="parkchirp--payor--reassign-primary-contact--operation"
                containerClasses={ ['parkchirp--contact-info-panel-field--reassign-primary-contact', 'parkchirp--reassign-primary-contact--control'] }
                layout="columns"
              >
                <InputSelectField
                  inputId="parkchirp--reassign-primary-contact--level"
                  value={level}
                  options={[
                    { label: 'Reassign all subscribers', value: 'user' },
                    { label: 'Reassign current subscriber', value: 'payor' },
                    { label: 'Reassign current subscription', value: 'subscription' },
                  ]}
                  onChange={(value) => setLevel(value) }
                  editMode={true}
                  disabledMode={false}
                />
              </Field>
            </div>
          }
          <div className="parkchirp--fields--reassign-primary-contact">
            <ActionButton label={ confirmMode ? 'Confirm' : 'Search' }
              classNames={ ['parkchirp--action-button--search-email'] }
              inProgressState={ queryInProgress }
              onClick={ confirmMode ? onConfirm : onSearch }
            />
            <button type="button"
              className="parkchirp--button-as-link"
              onClick={ onCancel }>
              Cancel
            </button>
          </div>
        </>
      }
      { successMode &&
        <div className="parkchirp--fields--reassign-primary-contact">
          <ActionButton label="OK"
            classNames={ ['parkchirp--action-button--ok'] }
            onClick={ onCancel }
          />
        </div>
      }
    </div>
  )
};

export default SubscriptionDetailReassignPanel;